import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { BellRing, ChevronLeft, ChevronRight } from "lucide-react";
import { api, errMsg } from "@/lib/api";
import { fmtDate } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { stateText } from "@/components/RiderDocs";

const KINDS = { assessment: ["Assessment", "bg-sky-500/20 text-sky-400 border-sky-500"], licence: ["Licence check", "bg-violet-500/20 text-violet-400 border-violet-500"],
  insurance: ["Insurance", "bg-emerald-500/20 text-emerald-400 border-emerald-500"], advanced: ["Advanced", "bg-orange-500/20 text-orange-400 border-orange-500"] };
const ym = (d) => d.toISOString().slice(0, 7);
const shift = (m, n) => { const d = new Date(`${m}-01T12:00`); d.setMonth(d.getMonth() + n); return ym(d); };

const Remind = ({ rid }) => {
  const [busy, setBusy] = useState(false);
  const go = async (e) => {
    e.preventDefault(); setBusy(true);
    try { const { data } = await api.post(`/rider-docs/remind/${rid}`); toast.success(`Reminder sent${data.emailed ? " by email" : ""}${data.pushed ? " and app alert" : ""}`); } catch (err) { toast.error(errMsg(err)); }
    setBusy(false);
  };
  return <button onClick={go} disabled={busy} data-testid={`remind-${rid}`} className="inline-flex items-center gap-1 rounded-full border px-2.5 py-1 text-[11px] font-bold disabled:opacity-50"><BellRing className="h-3 w-3" />Remind</button>;
};

const Chip = ({ i, manage }) => (
  <Link to={`/riders/${i.rider_id}`} data-testid={`cal-item-${i.rider_id}-${i.kind}`} className={`block truncate rounded border-l-4 px-1.5 py-0.5 text-[11px] font-semibold ${KINDS[i.kind][1]}`} title={`${i.name} · ${i.label}`}>
    {i.name} · {KINDS[i.kind][0]}{manage && i.state !== "ok" ? " !" : ""}
  </Link>
);

const Grid = ({ month, items, manage }) => {
  const first = new Date(`${month}-01T12:00`);
  const pad = (first.getDay() + 6) % 7;
  const days = new Date(first.getFullYear(), first.getMonth() + 1, 0).getDate();
  const today = new Date().toLocaleDateString("en-CA");
  const cells = [...Array(pad).fill(null), ...Array.from({ length: days }, (_, k) => `${month}-${String(k + 1).padStart(2, "0")}`)];
  return (
    <div className="hidden grid-cols-7 gap-1 md:grid" data-testid="renewals-grid">
      {["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"].map((d) => <p key={d} className="px-1 text-xs font-bold uppercase text-muted-foreground">{d}</p>)}
      {cells.map((d, k) => (
        <div key={d || `p${k}`} className={`min-h-24 min-w-0 space-y-1 rounded-lg border p-1 ${!d ? "border-transparent" : d === today ? "border-primary" : "bg-card"}`} data-testid={d ? `cal-day-${d}` : undefined}>
          {d && <p className="text-xs font-bold text-muted-foreground">{Number(d.slice(8))}</p>}
          {d && items.filter((i) => i.expires === d).map((i) => <Chip key={`${i.rider_id}-${i.kind}`} i={i} manage={manage} />)}
        </div>
      ))}
    </div>
  );
};

const List = ({ items, manage }) => (
  <ul className="space-y-2 md:hidden" data-testid="renewals-list">
    {items.length === 0 && <li className="text-sm text-muted-foreground">Nothing runs out this month.</li>}
    {items.map((i) => (
      <li key={`${i.rider_id}-${i.kind}`} className={`flex items-center gap-2 rounded-xl border-l-4 p-3 ${KINDS[i.kind][1]}`}>
        <div className="min-w-0 flex-1"><p className="font-mono text-xs">{fmtDate(i.expires)}</p><Link to={`/riders/${i.rider_id}`} className="block truncate font-bold text-foreground">{i.name}</Link><p className="text-xs">{i.label}</p></div>
        {manage && i.state === "soon" && <Remind rid={i.rider_id} />}
      </li>
    ))}
  </ul>
);

export default function Renewals() {
  const { user } = useAuth();
  const manage = (user.permissions?.riders || 0) >= 2;
  const [month, setMonth] = useState(ym(new Date()));
  const [kinds, setKinds] = useState(Object.keys(KINDS));
  const [d, setD] = useState(null);
  useEffect(() => {
    let live = true;
    setD(null);
    api.get(`/rider-docs/calendar?month=${month}`).then((r) => { if (live) setD(r.data); }).catch((e) => toast.error(errMsg(e)));
    return () => { live = false; };
  }, [month]);
  const items = (d?.items || []).filter((i) => kinds.includes(i.kind));
  const overdue = (d?.overdue || []).filter((i) => kinds.includes(i.kind));
  const byRider = Object.values(overdue.reduce((a, i) => ({ ...a, [i.rider_id]: { id: i.rider_id, name: i.name, items: [...(a[i.rider_id]?.items || []), i] } }), {}));
  const label = new Date(`${month}-01T12:00`).toLocaleDateString("en-GB", { month: "long", year: "numeric" });
  return (
    <div className="space-y-6" data-testid="renewals-page">
      <div><p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Lead Assessor · planning</p><h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Renewals calendar</h1></div>
      <div className="flex flex-wrap items-center gap-3">
        <button onClick={() => setMonth(shift(month, -1))} data-testid="renewals-prev" className="rounded-full border p-2"><ChevronLeft className="h-4 w-4" /></button>
        <p className="min-w-40 text-center text-lg font-bold" data-testid="renewals-month">{label}</p>
        <button onClick={() => setMonth(shift(month, 1))} data-testid="renewals-next" className="rounded-full border p-2"><ChevronRight className="h-4 w-4" /></button>
        <button onClick={() => setMonth(ym(new Date()))} data-testid="renewals-today" className="rounded-full border px-3 py-1.5 text-xs font-bold">This month</button>
        <span className="text-sm text-muted-foreground" data-testid="renewals-count">{items.length} renewal{items.length === 1 ? "" : "s"} this month</span>
      </div>
      <div className="flex flex-wrap gap-2">{Object.entries(KINDS).map(([k, [l, c]]) => (
        <button key={k} onClick={() => setKinds(kinds.includes(k) ? kinds.filter((x) => x !== k) : [...kinds, k])} data-testid={`renewals-filter-${k}`} className={`rounded-full border px-3 py-1 text-xs font-bold ${kinds.includes(k) ? c : "opacity-40"}`}>{l}</button>
      ))}</div>
      {!d ? <div className="h-64 animate-pulse rounded-2xl bg-card" /> : <><Grid month={month} items={items} manage={manage} /><List items={items} manage={manage} /></>}
      {overdue.length > 0 && (
        <section className="space-y-2 rounded-2xl border border-red-600 bg-red-600/10 p-4" data-testid="renewals-overdue">
          <h2 className="font-bold text-red-500">Already expired or no date - these riders can't ride ({overdue.length})</h2>
          <ul className="space-y-3 text-sm">{byRider.map((r) => (
            <li key={r.id} className="space-y-1" data-testid={`overdue-rider-${r.id}`}><div className="flex flex-wrap items-center gap-2"><Link to={`/riders/${r.id}`} className="font-bold underline-offset-2 hover:underline">{r.name}</Link>{manage && <Remind rid={r.id} />}</div>
              {r.items.map((i) => <p key={i.kind} className="text-muted-foreground">{i.label}: {stateText(i)}</p>)}</li>
          ))}</ul>
        </section>
      )}
    </div>
  );
}
